import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { useDeleteWorkspaceLogo, useUpdateWorkspace, useUploadWorkspaceLogo } from "@/api/admin/workspace-mutations";
import { LogoUploader } from "@/components/application/logo-uploader/logo-uploader";
import { Select } from "@/components/base/select/select";
import { TabHeader } from "@/pages/admin/merchant-detail/tabs/tab-header";

const CUSTOMER_TYPE_OPTIONS = [
  { id: "brand" as const, label: "Merchant" },
  { id: "agency" as const, label: "Agency" },
];

type WorkspaceDetailsTabProps = {
  workspace: {
    id: string;
    name: string;
    logoUrl?: string | null;
    customerType?: "brand" | "agency" | null;
  };
};

export const WorkspaceDetailsTab = ({ workspace }: WorkspaceDetailsTabProps) => {
  const navigate = useNavigate();
  const [name, setName] = useState(workspace.name);
  const [customerType, setCustomerType] = useState<"brand" | "agency">(workspace.customerType ?? "brand");
  const [hasChanges, setHasChanges] = useState(false);

  const updateWorkspace = useUpdateWorkspace();
  const uploadLogo = useUploadWorkspaceLogo();
  const deleteLogo = useDeleteWorkspaceLogo();

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setHasChanges(e.target.value !== workspace.name || customerType !== (workspace.customerType ?? "brand"));
  };

  const handleCustomerTypeChange = (value: "brand" | "agency") => {
    setCustomerType(value);
    setHasChanges(name !== workspace.name || value !== (workspace.customerType ?? "brand"));
  };

  const handleSave = async () => {
    try {
      await updateWorkspace.mutateAsync({ id: workspace.id, name, customerType });
      setHasChanges(false);
      toast.success("Workspace updated successfully");
    } catch {
      toast.error("Failed to update workspace");
    }
  };

  const handleCancel = () => {
    navigate({ to: "/admin/customers" });
  };

  const handleUpload = async (file: File) => {
    try {
      await uploadLogo.mutateAsync({ workspaceId: workspace.id, file });
    } catch {
      toast.error("Failed to upload logo");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteLogo.mutateAsync(workspace.id);
    } catch {
      toast.error("Failed to delete logo");
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <TabHeader
        title="Company Workspace Details"
        subtitle="These are the details for the Workspace"
        hasChanges={hasChanges}
        isPending={updateWorkspace.isPending}
        onSave={handleSave}
        onCancel={handleCancel}
      />

      <div className="border-t border-primary" />

      <div className="flex flex-row gap-8">
        <div className="w-80 flex flex-col gap-1">
          <p className="text-sm font-medium text-primary">
            Company Workspace Name <span className="text-error">*</span>
          </p>
          <p className="text-sm text-tertiary">
            This is how it will look in the navigation so make sure caps and spelling are as desired
          </p>
        </div>
        <div className="flex-1 max-w-md">
          <input
            type="text"
            value={name}
            onChange={handleNameChange}
            className="w-full px-3 py-2 text-md text-primary bg-primary rounded-lg ring-1 ring-primary outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="border-t border-primary" />

      <div className="flex flex-row gap-8">
        <div className="w-80 flex flex-col gap-1">
          <p className="text-sm font-medium text-primary">
            Customer type <span className="text-error">*</span>
          </p>
          <p className="text-sm text-tertiary">Is this workspace for a merchant or an agency?</p>
        </div>
        <div className="flex-1 max-w-md">
          <Select
            label=""
            placeholder="Select customer type"
            size="md"
            items={CUSTOMER_TYPE_OPTIONS}
            selectedKey={customerType}
            onSelectionChange={(v) => handleCustomerTypeChange(v as "brand" | "agency")}
            popoverClassName="dark-mode"
          >
            {(item) => (
              <Select.Item key={item.id} id={item.id}>
                {item.label}
              </Select.Item>
            )}
          </Select>
        </div>
      </div>

      <div className="border-t border-primary" />

      <div className="flex flex-row gap-8">
        <div className="w-80 flex flex-col gap-1">
          <p className="text-sm font-medium text-primary">
            Company logo <span className="text-error">*</span>
          </p>
          <p className="text-sm text-tertiary">This is the logo that will appear in the navigation for all users</p>
        </div>
        <LogoUploader
          currentLogoUrl={workspace.logoUrl}
          onUpload={handleUpload}
          onDelete={handleDelete}
          isUploading={uploadLogo.isPending}
          isDeleting={deleteLogo.isPending}
        />
      </div>
    </div>
  );
};
